import { useCallback, useEffect, useState } from 'react'
import { User, useUser } from '@supabase/auth-helpers-react'
import { ButtonWithIcon } from './input'
import { addRealTimeListener } from '@/common/realtime'
import { getAllNotes } from '@/common/notes'
import { handleAPIRequest, localAPIRequest } from '@/common/request'
import {
  createNoteLinkWithID,
  formatDate,
  getRelativeTimeString,
} from '@/common/utils'
import { SingleNote } from 'contexts/note'
import Link from 'next/link'
import { PATHS } from '@/common/const'

const realtimeUpdateTime = 30000

export function useCheckInTime() {
  const [, forceRender] = useState(0)
  const [checkInTime, setCheckInTime] = useState<string | null>(null)
  const user = useUser()

  const fetchData = useCallback(() => {
    if (!user?.id) return

    localAPIRequest({
      data: null,
      action: 'getCheckInTime',
    }).then((response) => {
      if (response.ok) {
        setCheckInTime(response.data)
      }
    })
  }, [user])

  useEffect(() => {
    fetchData()

    const profilesListener = addRealTimeListener({
      table: 'profiles',
      callback: fetchData,
    })

    return () => {
      profilesListener.unsubscribe()
    }
  }, [])

  useEffect(() => {
    const timer = setTimeout(() => forceRender(Date.now()), realtimeUpdateTime)
    return () => clearTimeout(timer)
  }, [])

  return checkInTime
}

export function useAutoCheckin(user: User | null) {
  useEffect(() => {
    if (user?.id) {
      localAPIRequest({
        action: 'checkin',
        data: null,
      })
    }
  }, [user])
}

function CheckInButton() {
  const [isBusy, setBusy] = useState(false)
  const doCheckin = useCallback(() => {
    handleAPIRequest(
      localAPIRequest({
        action: 'checkin',
        data: null,
      }),
      {
        busyState: setBusy,
        successMessage: 'Checked-in!',
        loadingMessage: 'Checking in ...',
        errorMessage:
          "We could not check you in. Please try again. Contact us if it's not fixed.",
      },
    )
  }, [])

  return (
    <ButtonWithIcon isDisabled={isBusy} onClick={doCheckin}>
      Check-in now
    </ButtonWithIcon>
  )
}

export function NavCheckInStatus() {
  const checkInTime = useCheckInTime()
  const user = useUser()

  if (!user) return null

  return (
    <Link
      href={PATHS.ACCOUNT}
      className="flex items-center gap-2 rounded-md px-2 py-1 text-sm text-gray-300 transition hover:text-white"
      title="View check-in details"
    >
      <span className="relative flex h-2 w-2">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-green-500" />
      </span>
      <span className="hidden sm:inline">
        {checkInTime ? 'Checked in' : 'Checking in...'}
      </span>
    </Link>
  )
}

export function ListNotesWithGracePeriod() {
  const [notes, setNotes] = useState<SingleNote[]>([])

  useEffect(() => {
    getAllNotes().then((n) =>
      setNotes(n.data?.filter((note: SingleNote) => note?.is_active) ?? []),
    )
  }, [])

  return (
    <div className="border-t border-color-only divided-y">
      {notes?.map((note) => (
        <div className="p-4 flex justify-between" key={note?.id}>
          <div>
            <Link href={createNoteLinkWithID(note?.id as string)}>
              {note?.note_title}
            </Link>
          </div>
          <div className="text-sm">
            Checked-in till{' '}
            <span className="text-green-600 ml-1">
              {formatDate(note?.grace_expires_at, {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </span>
          </div>
        </div>
      ))}
    </div>
  )
}

export function DisplayCheckIn() {
  const checkInTime = useCheckInTime()

  return (
    <div className="flex justify-between items-center">
      <div>
        Last checked-in{' '}
        <b>
          {checkInTime ? getRelativeTimeString(new Date(checkInTime)) : '...'}
        </b>
      </div>
      <div>
        <CheckInButton />
      </div>
    </div>
  )
}
